import { Container, Graphics } from 'pixi.js';
import { BaseScene } from './BaseScene.js';
import { MenuScene } from './MenuScene.js';
import { GameOverScene } from './GameOverScene.js';
import { GameState } from '../core/GameState.js';
import { DebugLog } from '../core/DebugLog.js';
import { GAME_WIDTH, GAME_HEIGHT, TILE_SIZE } from '../main.js';
import { DungeonGenerator, TILE_STAIRS, TILE_WALL, TILE_DOOR } from '../systems/DungeonGenerator.js';
import { DungeonRenderer } from '../systems/DungeonRenderer.js';
import { Player } from '../entities/Player.js';
import { Enemy } from '../entities/Enemy.js';
import { Boss } from '../entities/Boss.js';
import { CombatSystem } from '../systems/CombatSystem.js';
import { AbilitySystem } from '../systems/AbilitySystem.js';
import { DamageNumbers } from '../systems/DamageNumbers.js';
import { Pathfinding } from '../systems/Pathfinding.js';
import { NPC } from '../entities/NPC.js';
import { Projectile } from '../entities/Projectile.js';
import { HUD } from '../ui/HUD.js';
import { ShopPanel } from '../ui/ShopPanel.js';
import { InventoryPanel } from '../ui/InventoryPanel.js';
import { ItemManager } from '../systems/ItemManager.js';
import { LootTable } from '../systems/LootTable.js';
import { StatusEffectSystem } from '../systems/StatusEffectSystem.js';
import { AoESystem } from '../systems/AoESystem.js';
import { RelicSystem } from '../systems/RelicSystem.js';
import { LevelSystem } from '../systems/LevelSystem.js';
import { LevelUpPanel } from '../ui/LevelUpPanel.js';
import { Minimap } from '../ui/Minimap.js';
import { SeededRNG } from '../utils/SeededRNG.js';
import { ScreenShake } from '../utils/ScreenShake.js';
import { ParticleSystem } from '../systems/ParticleSystem.js';
import { ComboSystem } from '../systems/ComboSystem.js';
import { AchievementSystem } from '../systems/AchievementSystem.js';
import { SkillTreePanel } from '../ui/SkillTreePanel.js';
import { AchievementPanel } from '../ui/AchievementPanel.js';
import enemyData from '../data/enemies.json';
import itemsData from '../data/items.json';
import classesData from '../data/classes.json';
import relicsData from '../data/relics.json';
import unlocksData from '../data/unlocks.json';

const BASE_DUNGEON_WIDTH = 60;  // tiles
const BASE_DUNGEON_HEIGHT = 40; // tiles

/** Auto-save interval in seconds. */
const AUTO_SAVE_INTERVAL = 30;

export class GameplayScene extends BaseScene {
  init() {
    DebugLog.info('GameplayScene', 'Initializing gameplay');

    // World container -- camera moves this
    this.world = new Container();
    this.container.addChild(this.world);

    // Floor progression state
    this.bossSpawned = false;
    this.bossDefeated = false;
    this.boss = null;
    this.roomsCleared = 0;
    this.totalClearableRooms = 0;
    this.roomEnemyCounts = new Map(); // room index -> remaining enemy count
    this.bossRoomDoors = []; // positions of doors leading to boss room, blocked until rooms cleared
    this.transitioning = false; // prevent input during floor transition

    // NPC tracking (M15)
    this.npcs = [];

    // Projectile tracking (M16)
    this.projectiles = [];

    // Shop state (M15)
    this.shopOpen = false;

    // Auto-save timer (M10)
    this.autoSaveTimer = 0;

    // Generate dungeon (using saved seed if restoring)
    this._generateDungeon();

    // Load class data (M11)
    const classId = GameState.selectedClass || 'ironclad';
    const classInfo = classesData[classId] || classesData['ironclad'];
    const classData = { ...classInfo, _id: classId };

    // Create player entity with class data
    const entranceRoom = this.dungeon.rooms.find(r => r.type === 'entrance') || this.dungeon.rooms[0];
    const spawnX = entranceRoom.centerX * TILE_SIZE;
    const spawnY = entranceRoom.centerY * TILE_SIZE;

    this.player = new Player(spawnX, spawnY, classData);
    this.world.addChild(this.player.container);

    // Sync player HP to GameState (restore saved HP if continuing, or set from class)
    if (GameState.playerHP > 0 && GameState.playerMaxHP > 0) {
      this.player.hp = GameState.playerHP;
      this.player.maxHp = GameState.playerMaxHP;
    } else {
      // Fresh run: set GameState from class stats
      GameState.playerHP = this.player.hp;
      GameState.playerMaxHP = this.player.maxHp;
    }

    // Wire up damage callback for screen effects
    this.player.onDamaged = (amount) => {
      this._onPlayerDamaged(amount);
    };

    // Equip starting weapon from class data or GameState (M13: use equipment system)
    if (GameState.weapon) {
      const weaponData = itemsData.weapons[GameState.weapon];
      if (weaponData) {
        this.player.equipItem('weapon', weaponData, GameState.weapon);
      }
    } else {
      // Use class starting weapon
      const startWeaponId = classInfo.startingWeapon || 'rusty_sword';
      const startWeapon = itemsData.weapons[startWeaponId] || itemsData.weapons.rusty_sword;
      this.player.equipItem('weapon', startWeapon, startWeaponId);
      GameState.weapon = startWeaponId;
    }

    // Restore inventory from GameState, or apply starting items for new runs
    if (GameState.inventory.length > 0) {
      this.player.inventory = GameState.inventory.slice();
    } else if (classInfo.startingItems && classInfo.startingItems.length > 0) {
      // Apply class starting items
      for (const itemId of classInfo.startingItems) {
        const itemData = itemsData.consumables[itemId];
        if (itemData && this.player.inventory.length < this.player.maxInventory) {
          this.player.inventory.push({ itemId, itemData });
        }
      }
      GameState.inventory = this.player.inventory.slice();
    }

    // Create item manager
    this.itemManager = new ItemManager(this.world, this.eventBus);

    // RNG for loot drops
    this.lootRng = new SeededRNG(Date.now() + 7919);

    // Create damage numbers system
    this.damageNumbers = new DamageNumbers(this.world);

    // Create status effect system (M18)
    this.statusEffectSystem = new StatusEffectSystem(this.eventBus);

    // Create combat system (with status effect system reference for frozen bonus)
    this.combatSystem = new CombatSystem(this.eventBus, this.damageNumbers, this.statusEffectSystem);

    // Create AoE system (M20)
    this.aoeSystem = new AoESystem(this.world);

    // Create ability system (M12)
    this.abilitySystem = new AbilitySystem(this.eventBus);

    // Ability visual effects container (M12)
    this.abilityEffects = [];

    // HUD (HTML overlay)
    const uiRoot = document.getElementById('ui-root');
    this.hud = new HUD(uiRoot);

    // Inventory panel (M14)
    this.inventoryPanel = new InventoryPanel(uiRoot);
    this.inventoryOpen = false;

    // Shop panel (M15)
    this.shopPanel = new ShopPanel(uiRoot);

    // Level-up panel (M22)
    this.levelUpPanel = new LevelUpPanel(uiRoot);

    // Skill tree panel (M28)
    this.skillTreePanel = new SkillTreePanel(uiRoot);
    this.skillTreePanel.onAllocate = () => {
      // Recalculate player stats when skills are allocated
      this.player._applyEffectiveStats();
    };

    // Achievement system (M28)
    this.achievementSystem = new AchievementSystem(this.eventBus);
    this.achievementPanel = new AchievementPanel(uiRoot);

    // Relic system (M21)
    this.relicSystem = new RelicSystem(this.eventBus);
    // Restore relics from GameState
    if (GameState.relics && GameState.relics.length > 0) {
      for (const r of GameState.relics) {
        this.relicSystem.equip(r.relicId, r.relicData);
      }
    }
    this._applyRelicModifiers();

    // Apply stat allocations from GameState to player (M22)
    this.player.statAllocations = { ...GameState.statAllocations };

    // Apply meta-progression unlock bonuses (M27)
    this.player.unlockBonuses = GameState.getUnlockBonuses(unlocksData);

    this.player._applyEffectiveStats();
    // Sync maxHp to GameState after stat application
    GameState.playerMaxHP = this.player.maxHp;

    // Minimap (M23) -- rendered on PixiJS stage, not world (stays fixed)
    this.minimap = new Minimap(this.container, GAME_WIDTH);

    // Restore equipment from GameState (M13)
    this._restoreEquipment();

    // Setup minimap with dungeon data (M23)
    if (this.minimap) {
      const dw = this.dungeon.width || BASE_DUNGEON_WIDTH;
      const dh = this.dungeon.height || BASE_DUNGEON_HEIGHT;
      this.minimap.setDungeon(this.dungeon.rooms, dw, dh);
    }

    // Camera offset
    this.cameraX = 0;
    this.cameraY = 0;

    // Screen shake (M26 - replaces old timer-based shake)
    this.screenShake = new ScreenShake();

    // Particle system (M26)
    this.particleSystem = new ParticleSystem(this.world);

    // Combo system (M26)
    this.comboSystem = new ComboSystem();

    // Room tracking
    this.currentRoom = null;
    this._updateCurrentRoom();

    this._updateCamera();

    DebugLog.info('GameplayScene', `Gameplay ready -- ${this.dungeon.rooms.length} rooms, ${this.enemies.length} enemies, floor ${GameState.floor}`);
  }

  /**
   * Generate the dungeon, render it, spawn enemies, and set up room tracking.
   * Called on init and on each floor transition.
   * Uses GameState.dungeonSeed + floor to produce deterministic dungeons for save/load.
   */
  _generateDungeon() {
    const seed = GameState.dungeonSeed + GameState.floor * 9973;
    const floor = GameState.floor;
    this.dungeon = DungeonGenerator.generate(BASE_DUNGEON_WIDTH, BASE_DUNGEON_HEIGHT, seed, floor);

    // Use the actual generated dimensions (may be scaled up)
    const actualWidth = this.dungeon.width || BASE_DUNGEON_WIDTH;
    const actualHeight = this.dungeon.height || BASE_DUNGEON_HEIGHT;

    // Render dungeon tiles (initial render uses fallback colors for map objects)
    this.dungeonRenderer = new DungeonRenderer();
    this.dungeonContainer = this.dungeonRenderer.render(this.dungeon);
    this.world.addChild(this.dungeonContainer);

    // Load map object sprites and re-render when ready
    this.dungeonRenderer.loadAssets().then(() => {
      this._rerenderDungeon();
    });

    // Create pathfinding instance for enemy AI
    this.pathfinding = new Pathfinding(this.dungeon.tiles);

    // Dungeon pixel dimensions for camera clamping
    this.dungeonPixelW = actualWidth * TILE_SIZE;
    this.dungeonPixelH = actualHeight * TILE_SIZE;

    // Spawn enemies from dungeon room data
    this.enemies = [];
    this._spawnEnemies();

    // Spawn NPCs from room data (M15)
    this.npcs = [];
    this._spawnNPCs();

    // Reset projectiles
    this.projectiles = [];

    // Block boss room doors until all standard rooms are cleared
    this._setupBossRoomDoors();

    // Track room enemy counts for room-cleared detection
    this._initRoomTracking();
  }

  /**
   * Initialize room tracking: count enemies per room, identify clearable rooms.
   */
  _initRoomTracking() {
    this.roomEnemyCounts = new Map();
    this.roomsCleared = 0;
    this.totalClearableRooms = 0;

    for (let i = 0; i < this.dungeon.rooms.length; i++) {
      const room = this.dungeon.rooms[i];
      if (room.type === 'entrance' || room.type === 'boss' || room.type === 'shop' || room.type === 'treasure') continue;

      const enemyCount = room.enemies ? room.enemies.length : 0;
      this.roomEnemyCounts.set(i, enemyCount);
      this.totalClearableRooms++;

      // If room had no enemies, it counts as already cleared
      if (enemyCount === 0) {
        this.roomsCleared++;
      }
    }
  }

  /**
   * Find and block doors leading to the boss room by replacing them with walls.
   * Store their positions so we can restore them when all rooms are cleared.
   */
  _setupBossRoomDoors() {
    this.bossRoomDoors = [];
    const bossRoom = this.dungeon.rooms.find(r => r.type === 'boss');
    if (!bossRoom) return;

    const tiles = this.dungeon.tiles;
    const height = tiles.length;
    const width = tiles[0].length;

    // Scan the perimeter of the boss room (and one tile outside) for door tiles
    for (let row = bossRoom.y - 1; row <= bossRoom.y + bossRoom.height; row++) {
      for (let col = bossRoom.x - 1; col <= bossRoom.x + bossRoom.width; col++) {
        if (row < 0 || row >= height || col < 0 || col >= width) continue;
        if (tiles[row][col] === TILE_DOOR) {
          // Check if this door is on the boss room boundary
          const isOnBorder = (
            row === bossRoom.y - 1 || row === bossRoom.y + bossRoom.height ||
            col === bossRoom.x - 1 || col === bossRoom.x + bossRoom.width
          );
          // Also check doors just inside the boss room edge
          const isJustInside = (
            row === bossRoom.y || row === bossRoom.y + bossRoom.height - 1 ||
            col === bossRoom.x || col === bossRoom.x + bossRoom.width - 1
          );
          if (isOnBorder || isJustInside) {
            this.bossRoomDoors.push({ row, col, originalTile: TILE_DOOR });
            tiles[row][col] = TILE_WALL;
          }
        }
      }
    }

    // If we blocked doors, re-render the dungeon to show the walls
    if (this.bossRoomDoors.length > 0) {
      this._rerenderDungeon();

      // Rebuild pathfinding with updated tiles
      this.pathfinding = new Pathfinding(this.dungeon.tiles);
    }
  }

  /**
   * Unblock boss room doors when all standard rooms are cleared.
   */
  _unblockBossRoom() {
    if (this.bossRoomDoors.length === 0) return;

    const tiles = this.dungeon.tiles;
    for (const door of this.bossRoomDoors) {
      tiles[door.row][door.col] = door.originalTile;
    }
    this.bossRoomDoors = [];

    // Re-render dungeon with restored doors
    this._rerenderDungeon();

    // Rebuild pathfinding
    this.pathfinding = new Pathfinding(this.dungeon.tiles);

    DebugLog.info('GameplayScene', 'Boss room unlocked -- all rooms cleared');
  }

  /**
   * Re-render the dungeon container (tiles + map objects).
   * Replaces the existing dungeon container in the world at position 0.
   */
  _rerenderDungeon() {
    if (this.dungeonContainer && this.dungeonContainer.parent) {
      this.world.removeChild(this.dungeonContainer);
      this.dungeonContainer.destroy({ children: true });
    }
    this.dungeonContainer = this.dungeonRenderer.render(this.dungeon);
    this.world.addChildAt(this.dungeonContainer, 0);
  }

  /**
   * Called when player takes damage. Triggers screen shake.
   * @param {number} amount - Damage taken
   */
  _onPlayerDamaged(amount) {
    // Screen shake scaled to damage
    const intensity = Math.min(4, 1.5 + amount * 0.05);
    this.screenShake.shake(intensity, 0.15);

    // Trigger HUD damage flash
    if (this.hud) {
      this.hud.flashDamage();
    }
  }

  /**
   * Spawn Enemy instances from room.enemies data generated by DungeonGenerator.
   * Applies floor-based scaling to enemy stats.
   */
  _spawnEnemies() {
    const floor = GameState.floor;
    const scaling = 1 + (floor - 1) * 0.15;

    for (const room of this.dungeon.rooms) {
      if (!room.enemies) continue;

      for (const spawn of room.enemies) {
        const config = enemyData[spawn.type];
        if (!config) {
          DebugLog.error('GameplayScene', `Unknown enemy type: ${spawn.type}`);
          continue;
        }

        // Scale enemy stats by floor
        const scaledConfig = {
          ...config,
          hp: Math.round(config.hp * scaling),
          damage: Math.round(config.damage * scaling),
        };

        // Convert tile coords to pixel coords
        const pixelX = spawn.x * TILE_SIZE;
        const pixelY = spawn.y * TILE_SIZE;

        const enemy = new Enemy(pixelX, pixelY, scaledConfig);
        enemy.roomIndex = this.dungeon.rooms.indexOf(room);

        // Wire up ranged enemy projectile callback (M16)
        if (scaledConfig.isRanged) {
          enemy.onFireProjectile = (x, y, dx, dy, speed, damage) => {
            this._spawnProjectile(x, y, dx, dy, speed, damage, true);
          };
        }

        // Wire up AoE enemy callback (M16)
        if (scaledConfig.isAoe) {
          enemy.onAoeAttack = (x, y, radius, damage) => {
            this._handleAoeSlam(x, y, radius, damage);
          };
        }

        this.enemies.push(enemy);
        this.world.addChild(enemy.container);
      }
    }
  }

  /**
   * Spawn NPC entities from room data (M15).
   */
  _spawnNPCs() {
    for (const room of this.dungeon.rooms) {
      if (room.type !== 'shop' || !room.npc) continue;

      const npc = new NPC(room.npc.x * TILE_SIZE, room.npc.y * TILE_SIZE, {
        name: 'Merchant',
        color: 0x44aa88,
        size: 14,
        dialogue: 'Press E to browse wares.',
        npcType: room.npc.type,
      });
      this.npcs.push(npc);
      this.world.addChild(npc.container);
    }
  }

  /**
   * Spawn the boss in the boss room.
   */
  _spawnBoss() {
    if (this.bossSpawned) return;

    const bossRoom = this.dungeon.rooms.find(r => r.type === 'boss');
    if (!bossRoom) return;

    const floor = GameState.floor;
    const scaling = 1 + (floor - 1) * 0.15;

    const config = enemyData['lord_of_dust'];
    if (!config) {
      DebugLog.error('GameplayScene', 'Missing lord_of_dust enemy data');
      return;
    }

    const scaledConfig = {
      ...config,
      hp: Math.round(config.hp * scaling),
      damage: Math.round(config.damage * scaling),
    };

    const pixelX = bossRoom.centerX * TILE_SIZE;
    const pixelY = bossRoom.centerY * TILE_SIZE;

    this.boss = new Boss(pixelX, pixelY, scaledConfig);
    this.boss.state = 'chase'; // Boss immediately aggressive
    this.boss.onSpawnMinion = (boss) => this._spawnBossMinion(boss);
    this.boss.onFireBossProjectile = (x, y, dx, dy, speed, damage) => {
      this._spawnProjectile(x, y, dx, dy, speed, damage, true);
    };
    this.boss.onBossSlam = (x, y, radius, damage) => {
      this._handleAoeSlam(x, y, radius, damage);
    };
    this.enemies.push(this.boss);
    this.world.addChild(this.boss.container);

    this.bossSpawned = true;
    DebugLog.info('GameplayScene', `Boss spawned: ${config.name} (HP: ${scaledConfig.hp})`);
  }

  /**
   * Spawn a minion near the boss. Phase 1 spawns Tomb Rats, Phase 2 spawns Skeletons.
   */
  _spawnBossMinion(boss) {
    const floor = GameState.floor;
    const scaling = 1 + (floor - 1) * 0.15;

    // Phase 2 spawns skeletons instead of tomb rats (M17)
    const minionType = boss.phase >= 2 ? 'skeleton' : 'tomb_rat';
    const config = enemyData[minionType];
    if (!config) return;

    const scaledConfig = {
      ...config,
      hp: Math.round(config.hp * scaling),
      damage: Math.round(config.damage * scaling),
    };

    // Spawn near the boss with a small random offset
    const offsetX = (Math.random() * 2 - 1) * TILE_SIZE * 2;
    const offsetY = (Math.random() * 2 - 1) * TILE_SIZE * 2;
    const spawnX = boss.x + offsetX;
    const spawnY = boss.y + offsetY;

    const minion = new Enemy(spawnX, spawnY, scaledConfig);
    minion.state = 'chase'; // Minions immediately chase
    boss.activeMinions.push(minion);
    this.enemies.push(minion);
    this.world.addChild(minion.container);

    DebugLog.info('GameplayScene', `Boss spawned a ${config.name} minion`);
  }

  /**
   * Spawn a projectile into the world (M16, expanded M19).
   * @param {string} [projectileType] - Optional PROJECTILE_TYPES key for typed projectiles
   */
  _spawnProjectile(x, y, dx, dy, speed, damage, fromEnemy = true, projectileType = null) {
    const proj = new Projectile(x, y, dx, dy, speed, damage, fromEnemy, projectileType);
    this.projectiles.push(proj);
    this.world.addChild(proj.container);
  }

  /**
   * Handle an AoE slam from the boss (M17, updated M20 to use AoESystem).
   * Damages the player if within radius. Creates a visual effect.
   */
  _handleAoeSlam(x, y, radius, damage) {
    if (this.aoeSystem) {
      this.aoeSystem.createAoE(x, y, radius, damage, {
        color: 0xff4444,
        duration: 0.4,
        targets: [this.player],
        knockback: 1,
        damageNumbers: this.damageNumbers,
      });
    } else {
      // Fallback if AoE system not available
      const playerCX = this.player.x + TILE_SIZE / 2;
      const playerCY = this.player.y + TILE_SIZE / 2;
      const dx = playerCX - x;
      const dy = playerCY - y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= radius) {
        this.player.takeDamage(damage, x, y);
      }
    }

    this.damageNumbers.spawn(x, y - 12, 'SLAM!', 0xff6644);
  }

  /**
   * Handle floor transition: increment floor, regenerate dungeon, reposition player.
   * Auto-saves after transitioning to the new floor.
   */
  _transitionToNextFloor() {
    if (this.transitioning) return;
    this.transitioning = true;

    GameState.floor++;
    const nextFloor = GameState.floor;

    DebugLog.info('GameplayScene', `Transitioning to floor ${nextFloor}`);

    this.hud.showFloorTransition(nextFloor, () => {
      // This runs at the midpoint when screen is fully black

      // Clean up current floor
      this._cleanupFloor();

      // Generate new dungeon
      this._generateDungeon();

      // Reset boss state
      this.bossSpawned = false;
      this.bossDefeated = false;
      this.boss = null;

      // Move player to new entrance
      const entranceRoom = this.dungeon.rooms.find(r => r.type === 'entrance') || this.dungeon.rooms[0];
      this.player.x = entranceRoom.centerX * TILE_SIZE;
      this.player.y = entranceRoom.centerY * TILE_SIZE;
      this.player._syncVisual();

      // Re-add player to world (it was removed during cleanup)
      this.world.addChild(this.player.container);

      // Reset room tracking
      this.currentRoom = null;
      this._updateCurrentRoom();
      this._updateCamera();

      // Update minimap for new floor (M23)
      if (this.minimap) {
        const dw = this.dungeon.width || BASE_DUNGEON_WIDTH;
        const dh = this.dungeon.height || BASE_DUNGEON_HEIGHT;
        this.minimap.setDungeon(this.dungeon.rooms, dw, dh);
      }

      // Auto-save on floor transition (M10)
      GameState.playerHP = this.player.hp;
      GameState.save();

      // End transition after fade-out completes
      setTimeout(() => {
        this.transitioning = false;
      }, 500);
    });
  }

  /**
   * Clean up the current floor: remove enemies, dungeon graphics, etc.
   */
  _cleanupFloor() {
    // Remove all enemies
    for (const enemy of this.enemies) {
      if (enemy.container.parent) {
        this.world.removeChild(enemy.container);
      }
      enemy.container.destroy();
    }
    this.enemies = [];

    // Remove all NPCs (M15)
    for (const npc of this.npcs) {
      if (npc.container.parent) {
        this.world.removeChild(npc.container);
      }
      npc.container.destroy();
    }
    this.npcs = [];

    // Remove all projectiles (M16)
    for (const proj of this.projectiles) {
      if (proj.container.parent) {
        this.world.removeChild(proj.container);
      }
      proj.container.destroy();
    }
    this.projectiles = [];

    // Close shop if open (M15)
    if (this.shopPanel && this.shopPanel.isOpen) {
      this.shopPanel.close();
      this.shopOpen = false;
    }

    // Remove all item drops (M9)
    if (this.itemManager) {
      this.itemManager.clear();
    }

    // Clean up particles (M26)
    if (this.particleSystem) {
      this.particleSystem.destroy();
    }

    // Reset combo (M26)
    if (this.comboSystem) {
      this.comboSystem.reset();
    }

    // Remove dungeon graphics
    if (this.dungeonContainer && this.dungeonContainer.parent) {
      this.world.removeChild(this.dungeonContainer);
      this.dungeonContainer.destroy({ children: true });
    }

    // Remove player temporarily (will re-add after new dungeon)
    if (this.player.container.parent) {
      this.world.removeChild(this.player.container);
    }

    // Clean up ability visual effects (M12)
    if (this.abilityEffects) {
      for (const fx of this.abilityEffects) {
        if (fx.gfx.parent) fx.gfx.parent.removeChild(fx.gfx);
        fx.gfx.destroy();
      }
      this.abilityEffects = [];
    }

    // Clean up AoE system (M20)
    if (this.aoeSystem) {
      this.aoeSystem.destroy();
    }

    // Clear player status effects on floor transition (M18)
    if (this.player.statusEffects) {
      this.player.statusEffects.clear();
    }

    // Clean up damage numbers
    if (this.damageNumbers) {
      this.damageNumbers.destroy();
    }
    this.damageNumbers = new DamageNumbers(this.world);
    this.statusEffectSystem = new StatusEffectSystem(this.eventBus);
    this.combatSystem = new CombatSystem(this.eventBus, this.damageNumbers, this.statusEffectSystem);
    this.aoeSystem = new AoESystem(this.world);

    // Recreate item manager for new floor
    this.itemManager = new ItemManager(this.world, this.eventBus);
    this.lootRng = new SeededRNG(Date.now() + GameState.floor * 7919);
  }

  /**
   * Apply relic modifiers to player (M21).
   */
  _applyRelicModifiers() {
    if (!this.relicSystem || !this.player) return;
    this.player.relicModifiers = this.relicSystem.getModifiers();
    this.player.relics = this.relicSystem.getRelics();
    this.player._applyEffectiveStats();
  }

  /**
   * Sync relic state to GameState.
   */
  _syncRelicsToGameState() {
    GameState.relics = this.relicSystem.getRelics().map(r => ({ relicId: r.relicId, relicData: r.relicData }));
  }

  /**
   * Determine which room the player is currently in, if any.
   * Emits 'roomEntered' event when the player enters a new room.
   */
  _updateCurrentRoom() {
    const playerCenterX = this.player.x + TILE_SIZE / 2;
    const playerCenterY = this.player.y + TILE_SIZE / 2;
    const col = Math.floor(playerCenterX / TILE_SIZE);
    const row = Math.floor(playerCenterY / TILE_SIZE);

    for (const room of this.dungeon.rooms) {
      if (
        col >= room.x &&
        col < room.x + room.width &&
        row >= room.y &&
        row < room.y + room.height
      ) {
        if (this.currentRoom !== room) {
          this.currentRoom = room;
          this.eventBus.emit('roomEntered', room);
          DebugLog.info('GameplayScene', 'Entered room: ' + room.type);

          // Spawn boss when player enters the boss room
          if (room.type === 'boss' && !this.bossSpawned) {
            this._spawnBoss();
          }

          // Handle treasure room with relic drop (M21)
          if (room.type === 'treasure' && room.relicDrop && !room.relicClaimed) {
            room.relicClaimed = true;
            this._spawnRelicDrop(room);
          }
        }
        return;
      }
    }

    // Player is in a corridor -- no room
    if (this.currentRoom !== null) {
      this.currentRoom = null;
    }
  }

  /**
   * Check if the player is standing on a stairs tile.
   */
  _isPlayerOnStairs() {
    const playerCenterX = this.player.x + TILE_SIZE / 2;
    const playerCenterY = this.player.y + TILE_SIZE / 2;
    const col = Math.floor(playerCenterX / TILE_SIZE);
    const row = Math.floor(playerCenterY / TILE_SIZE);

    if (row < 0 || row >= this.dungeon.tiles.length || col < 0 || col >= this.dungeon.tiles[0].length) {
      return false;
    }

    return this.dungeon.tiles[row][col] === TILE_STAIRS;
  }

  /**
   * Mark a room as having one fewer enemy. If all enemies in the room are dead, mark it cleared.
   */
  _onEnemyKilledInRoom(roomIndex) {
    if (!this.roomEnemyCounts.has(roomIndex)) return;

    const remaining = this.roomEnemyCounts.get(roomIndex) - 1;
    this.roomEnemyCounts.set(roomIndex, remaining);

    if (remaining <= 0) {
      this.roomsCleared++;
      // Mark room cleared on minimap (M23)
      if (this.minimap) this.minimap.clearRoom(roomIndex);
      DebugLog.info('GameplayScene', `Room ${roomIndex} cleared (${this.roomsCleared}/${this.totalClearableRooms})`);

      // Check if all rooms are cleared to unblock boss room
      if (this.roomsCleared >= this.totalClearableRooms && this.bossRoomDoors.length > 0) {
        this._unblockBossRoom();
      }
    }
  }

  /**
   * Handle an item being picked up by the player (M9).
   * @param {ItemDrop} item - The picked-up item
   */
  _handlePickup(item) {
    const data = item.itemData;
    const pCenterX = this.player.x + TILE_SIZE / 2;
    const pCenterY = this.player.y + TILE_SIZE / 2;

    if (data.type === 'gold') {
      // Gold: auto-pickup, add to GameState
      const [minAmt, maxAmt] = data.amount || [1, 1];
      let amount = minAmt + Math.floor(Math.random() * (maxAmt - minAmt + 1));
      // Apply relic gold modifier (M21)
      if (this.relicSystem) {
        const mods = this.relicSystem.getModifiers();
        if (mods.goldMod > 0) {
          amount = Math.round(amount * (1 + mods.goldMod));
        }
      }
      GameState.gold += amount;
      GameState.goldCollected += amount;
      this.damageNumbers.spawn(pCenterX, pCenterY - 8, '+' + amount + ' gold', 0xffdd44);
      DebugLog.info('GameplayScene', `Picked up ${amount} gold`);
    } else if (data.type === 'relic') {
      // Relic pickup (M21)
      const relicId = item.itemId.replace('relic:', '');
      const relicData = relicsData[relicId];
      if (relicData && this.relicSystem) {
        if (this.relicSystem.equip(relicId, relicData)) {
          this._applyRelicModifiers();
          this._syncRelicsToGameState();
          this.damageNumbers.spawn(pCenterX, pCenterY - 8, '+' + relicData.name, parseInt(relicData.color, 16));
          DebugLog.info('GameplayScene', `Equipped relic: ${relicData.name}`);
        } else {
          // Full -- show message
          this.damageNumbers.spawn(pCenterX, pCenterY - 8, 'Relic slots full!', 0xff8888);
          // Put it back
          this.itemManager.addDrop(item.x, item.baseY || item.y, data, item.itemId);
        }
      }
    } else if (data.type === 'weapon' || data.type === 'armor' || data.type === 'accessory' || data.type === 'consumable') {
      // All non-gold items go to backpack inventory (M14)
      if (this.player.addToInventory({ itemId: item.itemId, itemData: data })) {
        this._syncEquipmentToGameState();
        const typeColors = { weapon: 0x88ccff, armor: 0x8B6914, accessory: 0xcc8844, consumable: 0xff4444 };
        const color = typeColors[data.type] || 0xcccccc;
        this.damageNumbers.spawn(pCenterX, pCenterY - 8, '+' + data.name, color);
        DebugLog.info('GameplayScene', `Picked up ${data.type}: ${data.name}`);
      } else {
        // Inventory full -- put item back
        this.itemManager.addDrop(item.x, item.baseY || item.y, data, item.itemId);
        this.damageNumbers.spawn(pCenterX, pCenterY - 8, 'Inventory Full!', 0xff8888);
      }
    }
  }

  /**
   * Handle visual effects returned from an ability execution (M12).
   * @param {object[]} effects - Array of effect descriptors
   */
  _handleAbilityEffects(effects) {
    for (const effect of effects) {
      if (effect.type === 'damage') {
        // Show damage number
        this.damageNumbers.spawn(effect.x, effect.y - 8, effect.amount, 0xffffff);
      } else if (effect.type === 'heal') {
        this.damageNumbers.spawn(effect.x, effect.y - 8, '+' + effect.amount + ' HP', 0x44ff44);
      } else if (effect.type === 'flash_circle') {
        // Create a temporary expanding circle graphic
        const gfx = new Graphics();
        gfx.circle(0, 0, effect.radius);
        gfx.fill({ color: effect.color, alpha: 0.4 });
        gfx.x = effect.x;
        gfx.y = effect.y;
        this.world.addChild(gfx);
        this.abilityEffects.push({
          gfx,
          timer: effect.duration || 0.3,
          maxTimer: effect.duration || 0.3,
          type: 'flash_circle',
        });
      } else if (effect.type === 'afterimage') {
        // Create an afterimage of the player shape at their old position
        const gfx = new Graphics();
        const half = this.player.size / 2;
        const cx = TILE_SIZE / 2;
        const cy = TILE_SIZE / 2;
        gfx.circle(cx, cy, half);
        gfx.fill({ color: effect.color, alpha: 0.5 });
        gfx.x = effect.x;
        gfx.y = effect.y;
        this.world.addChild(gfx);
        this.abilityEffects.push({
          gfx,
          timer: effect.duration || 0.4,
          maxTimer: effect.duration || 0.4,
          type: 'afterimage',
        });
      } else if (effect.type === 'particle_trail') {
        // Create a series of small dots between start and end
        const count = 5;
        for (let i = 0; i < count; i++) {
          const t = (i + 1) / (count + 1);
          const px = effect.startX + (effect.endX - effect.startX) * t;
          const py = effect.startY + (effect.endY - effect.startY) * t;
          const gfx = new Graphics();
          gfx.circle(0, 0, 2);
          gfx.fill({ color: effect.color, alpha: 0.6 });
          gfx.x = px;
          gfx.y = py;
          this.world.addChild(gfx);
          this.abilityEffects.push({
            gfx,
            timer: (effect.duration || 0.4) * (1 - t * 0.3),
            maxTimer: (effect.duration || 0.4) * (1 - t * 0.3),
            type: 'particle',
          });
        }
      } else if (effect.type === 'player_glow') {
        this.player.startGlow(effect.color, effect.duration);
      } else if (effect.type === 'apply_status') {
        // Apply status effect via StatusEffectSystem (M18)
        if (this.statusEffectSystem && effect.target && effect.effectId) {
          this.statusEffectSystem.apply(effect.target, effect.effectId);
        }
      }
    }
  }

  /**
   * Update and clean up ability visual effects (M12).
   * @param {number} dt - Delta time in seconds
   */
  _updateAbilityEffects(dt) {
    for (let i = this.abilityEffects.length - 1; i >= 0; i--) {
      const fx = this.abilityEffects[i];
      fx.timer -= dt;
      const progress = 1 - (fx.timer / fx.maxTimer);
      fx.gfx.alpha = Math.max(0, 1 - progress);

      if (fx.type === 'flash_circle') {
        // Scale up slightly as it fades
        const scale = 1 + progress * 0.3;
        fx.gfx.scale.set(scale, scale);
      }

      if (fx.timer <= 0) {
        if (fx.gfx.parent) {
          this.world.removeChild(fx.gfx);
        }
        fx.gfx.destroy();
        this.abilityEffects.splice(i, 1);
      }
    }
  }

  /**
   * Restore equipment from GameState onto the player (M13).
   */
  _restoreEquipment() {
    const eq = GameState.equipment;
    if (!eq) return;

    for (const slot of ['weapon', 'body', 'ring', 'amulet']) {
      if (eq[slot] && eq[slot].itemData) {
        this.player.equipment[slot] = { ...eq[slot] };
      }
    }

    // Sync weapon equipment slot with legacy weapon field
    if (this.player.equipment.weapon) {
      const w = this.player.equipment.weapon;
      this.player.equipWeapon(w.itemData, w.itemId);
    }

    // Apply effective stats from all equipment
    this.player._applyEffectiveStats();
  }

  /**
   * Sync player equipment state to GameState for save/load (M13).
   */
  _syncEquipmentToGameState() {
    GameState.equipment = {
      weapon: this.player.equipment.weapon ? { ...this.player.equipment.weapon } : null,
      body: this.player.equipment.body ? { ...this.player.equipment.body } : null,
      ring: this.player.equipment.ring ? { ...this.player.equipment.ring } : null,
      amulet: this.player.equipment.amulet ? { ...this.player.equipment.amulet } : null,
    };
    GameState.inventory = this.player.inventory.slice();
  }

  /**
   * Handle an action from the inventory panel (M14).
   * @param {object} action - { action: 'equip'|'drop'|'unequip'|'use', index, slot }
   */
  _handleInventoryAction(action) {
    const pCenterX = this.player.x + TILE_SIZE / 2;
    const pCenterY = this.player.y + TILE_SIZE / 2;

    if (action.action === 'equip') {
      const item = this.player.inventory[action.index];
      if (!item) return;

      // Remove from inventory
      this.player.inventory.splice(action.index, 1);

      // Equip and get previously equipped item
      const prev = this.player.equipItem(action.slot, item.itemData, item.itemId);

      // Put old item back in inventory if there was one
      if (prev) {
        this.player.addToInventory(prev);
      }

      // Sync weapon to GameState if weapon slot
      if (action.slot === 'weapon') {
        GameState.weapon = item.itemId;
      }

      this._syncEquipmentToGameState();
      this.damageNumbers.spawn(pCenterX, pCenterY - 12, 'Equipped ' + item.itemData.name, 0x88ccff);
      DebugLog.info('GameplayScene', `Equipped ${item.itemData.name} in ${action.slot} slot`);

    } else if (action.action === 'drop') {
      const item = this.player.inventory[action.index];
      if (!item) return;

      // Remove from inventory
      this.player.inventory.splice(action.index, 1);

      // Create item drop at player position (with pickup delay so it doesn't get re-collected)
      this.itemManager.addDrop(this.player.x, this.player.y, item.itemData, item.itemId, { pickupDelay: 0.8 });

      this._syncEquipmentToGameState();
      this.damageNumbers.spawn(pCenterX, pCenterY - 8, 'Dropped ' + item.itemData.name, 0x888888);
      DebugLog.info('GameplayScene', `Dropped ${item.itemData.name}`);

    } else if (action.action === 'unequip') {
      const prev = this.player.unequipItem(action.slot);
      if (!prev) return;

      // Try to add to inventory
      if (!this.player.addToInventory(prev)) {
        // Inventory full, drop on ground (with pickup delay)
        this.itemManager.addDrop(this.player.x, this.player.y, prev.itemData, prev.itemId, { pickupDelay: 0.8 });
        this.damageNumbers.spawn(pCenterX, pCenterY - 8, 'Inventory full! Dropped.', 0xff8888);
      }

      if (action.slot === 'weapon') {
        GameState.weapon = null;
      }

      this._syncEquipmentToGameState();
      this.damageNumbers.spawn(pCenterX, pCenterY - 12, 'Unequipped ' + prev.itemData.name, 0xcccccc);
      DebugLog.info('GameplayScene', `Unequipped ${prev.itemData.name} from ${action.slot}`);

    } else if (action.action === 'use') {
      const result = this.player.useConsumable(action.index);
      if (result) {
        if (result.healed && result.healed > 0) {
          this.damageNumbers.spawn(pCenterX, pCenterY - 8, '+' + result.healed + ' HP', 0x44ff44);
        }
        this._syncEquipmentToGameState();
        GameState.playerHP = this.player.hp;
      }
    }

    // Refresh the panel
    this.inventoryPanel.refresh();
  }

  /**
   * Spawn a random relic drop in a treasure room (M21).
   */
  _spawnRelicDrop(room) {
    const relicIds = Object.keys(relicsData);
    // Exclude relics already equipped
    const equipped = this.relicSystem.getRelics().map(r => r.relicId);
    const available = relicIds.filter(id => !equipped.includes(id));
    if (available.length === 0) return;

    const relicId = available[Math.floor(Math.random() * available.length)];
    const relicData = relicsData[relicId];

    // Create an item drop for the relic
    const dropData = {
      name: relicData.name,
      type: 'relic',
      color: relicData.color,
      description: relicData.description,
    };

    // Drop at room center
    const dropX = room.centerX * TILE_SIZE;
    const dropY = room.centerY * TILE_SIZE;
    this.itemManager.addDrop(dropX, dropY, dropData, 'relic:' + relicId);

    this.damageNumbers.spawn(dropX, dropY - 16, 'Relic!', parseInt(relicData.color, 16));
    DebugLog.info('GameplayScene', `Relic drop: ${relicData.name} in treasure room`);
  }

  /**
   * Open the level-up panel for stat allocation (M22).
   */
  _openLevelUpPanel() {
    if (!this.levelUpPanel) return;

    this.levelUpPanel.open(GameState, (stat) => {
      if (LevelSystem.allocateStat(GameState, stat)) {
        // Apply to player immediately
        this.player.statAllocations = { ...GameState.statAllocations };
        this.player._applyEffectiveStats();
        GameState.playerMaxHP = this.player.maxHp;
        GameState.playerHP = this.player.hp;
        // Refresh panel
        this.levelUpPanel.refresh(GameState);
      }
    });
  }

  _updateCamera() {
    // Center camera on player
    this.cameraX = this.player.x - GAME_WIDTH / 2 + TILE_SIZE / 2;
    this.cameraY = this.player.y - GAME_HEIGHT / 2 + TILE_SIZE / 2;

    // Clamp camera to dungeon bounds
    this.cameraX = Math.max(0, Math.min(this.dungeonPixelW - GAME_WIDTH, this.cameraX));
    this.cameraY = Math.max(0, Math.min(this.dungeonPixelH - GAME_HEIGHT, this.cameraY));

    // Apply to world container (negative offset to simulate camera movement + shake)
    this.world.x = -Math.round(this.cameraX) + this.screenShake.offsetX;
    this.world.y = -Math.round(this.cameraY) + this.screenShake.offsetY;
  }

  update(dt) {
    // Block all input during floor transition
    if (this.transitioning) return;

    const input = this.sceneManager.inputManager;

    // Handle shop input when open (M15) -- game pauses
    if (this.shopOpen && this.shopPanel && this.shopPanel.isOpen) {
      const purchase = this.shopPanel.handleInput(input, GameState.gold);
      if (purchase) {
        // Deduct gold, add item to inventory
        GameState.gold -= purchase.price;
        const added = this.player.addToInventory({ itemId: purchase.itemId, itemData: purchase.itemData });
        if (added) {
          this._syncEquipmentToGameState();
          const pCenterX = this.player.x + TILE_SIZE / 2;
          const pCenterY = this.player.y + TILE_SIZE / 2;
          this.damageNumbers.spawn(pCenterX, pCenterY - 8, 'Bought ' + purchase.itemData.name, 0x44aa88);
        } else {
          // Refund if inventory full
          GameState.gold += purchase.price;
          const pCenterX = this.player.x + TILE_SIZE / 2;
          const pCenterY = this.player.y + TILE_SIZE / 2;
          this.damageNumbers.spawn(pCenterX, pCenterY - 8, 'Inventory Full!', 0xff8888);
        }
      }
      if (!this.shopPanel.isOpen) {
        this.shopOpen = false;
      }
      this._updateHUD();
      return;
    }

    // M key toggles minimap (M23)
    if (input.isKeyJustPressed('KeyM')) {
      if (this.minimap) this.minimap.toggle();
    }

    // Tab toggles inventory (M14)
    if (input.isKeyJustPressed('Tab')) {
      this.inventoryOpen = !this.inventoryOpen;
      this.inventoryPanel.toggle(this.inventoryOpen, this.player);
    }

    // K toggles skill tree (M28)
    if (input.isKeyJustPressed('KeyK') && this.skillTreePanel) {
      if (this.skillTreePanel.isOpen) {
        this.skillTreePanel.close();
      } else {
        this.skillTreePanel.open();
      }
    }

    // Skill tree open = game paused
    if (this.skillTreePanel && this.skillTreePanel.isOpen) {
      if (input.isKeyJustPressed('Escape')) {
        this.skillTreePanel.close();
      }
      this._updateHUD();
      return;
    }

    // Close inventory with Escape if open, otherwise go to menu
    if (input.isKeyJustPressed('Escape') && this.inventoryOpen) {
      this.inventoryOpen = false;
      this.inventoryPanel.toggle(false, this.player);
      return;
    }

    // Handle inventory input when open (M14)
    if (this.inventoryOpen) {
      const action = this.inventoryPanel.handleInput(input);
      if (action) {
        this._handleInventoryAction(action);
      }
      // Game is paused while inventory is open -- only update HUD
      this._updateHUD();
      return;
    }

    // Escape returns to menu (save before leaving)
    if (input.isKeyJustPressed('Escape')) {
      DebugLog.info('GameplayScene', 'Returning to menu');
      GameState.playerHP = this.player.hp;
      GameState.save();
      GameState.isRunActive = false;
      this.sceneManager.switchTo(new MenuScene(this.sceneManager, this.eventBus));
      return;
    }

    // Update screen shake (M26)
    this.screenShake.update(dt);

    // Update particles (M26)
    this.particleSystem.update(dt);

    // Update combo system (M26)
    this.comboSystem.update(dt);

    // Auto-save timer (M10)
    this.autoSaveTimer += dt;
    if (this.autoSaveTimer >= AUTO_SAVE_INTERVAL) {
      this.autoSaveTimer = 0;
      GameState.playerHP = this.player.hp;
      GameState.playerMaxHP = this.player.maxHp;
      this._syncEquipmentToGameState();
      GameState.save();
    }

    // Check for stairs interaction (E key)
    if (input.isKeyJustPressed('KeyE')) {
      // Check stairs first
      if (this.bossDefeated && this._isPlayerOnStairs()) {
        this._transitionToNextFloor();
        return;
      }

      // Check NPC interaction (M15)
      for (const npc of this.npcs) {
        if (npc.isNearPlayer(this.player.x, this.player.y, 24)) {
          if (npc.npcType === 'merchant') {
            // Open shop
            const shopRng = new SeededRNG(GameState.dungeonSeed + GameState.floor * 1337);
            this.shopPanel.generateInventory(GameState.floor, shopRng);
            this.shopPanel.open(GameState.gold);
            this.shopOpen = true;
            return;
          }
        }
      }
    }

    // Check for attack input (left click or J key)
    if (input.mouse.justPressed || input.isKeyJustPressed('KeyJ')) {
      if (this.player.isRangedClass && this.player.projectileType) {
        // Ranged class (Riftmage): fire projectile toward mouse cursor (M19)
        if (this.player.attackCooldown <= 0 && !this.player.isDodging) {
          const playerCX = this.player.x + TILE_SIZE / 2;
          const playerCY = this.player.y + TILE_SIZE / 2;
          // Convert mouse screen position to world position
          const mouseWorldX = input.mouse.x + this.cameraX;
          const mouseWorldY = input.mouse.y + this.cameraY;
          const ddx = mouseWorldX - playerCX;
          const ddy = mouseWorldY - playerCY;
          const dist = Math.sqrt(ddx * ddx + ddy * ddy);
          if (dist > 0) {
            const ndx = ddx / dist;
            const ndy = ddy / dist;
            // Apply damage multiplier to projectile damage
            const [minDmg, maxDmg] = this.player.getDamageRange();
            const dmg = minDmg + Math.floor(Math.random() * (maxDmg - minDmg + 1));
            this._spawnProjectile(playerCX, playerCY, ndx, ndy, 0, dmg, false, this.player.projectileType);
            this.player.attackCooldown = this.player.attackCooldownTime;
            // Update facing direction toward mouse
            this.player.facingX = ndx;
            this.player.facingY = ndy;
          }
        }
      } else {
        // Melee class: standard attack
        if (this.player.startAttack()) {
          this.combatSystem.beginSwing();
        }
      }
    }

    // Check for ability input (Q key) (M12)
    if (input.isKeyJustPressed('KeyQ')) {
      const result = this.abilitySystem.executeAbility(
        this.player.classId,
        this.player,
        this.enemies,
        this.dungeon.tiles
      );
      if (result) {
        this._handleAbilityEffects(result.effects);
      }
    }

    // Update ability system cooldowns (M12)
    this.abilitySystem.update(dt);

    // Update ability readiness indicator on player (M12)
    this.player.abilityReady = this.abilitySystem.isReady(this.player.classId);

    // Track dodge state for relic on-dodge (M21)
    const wasDodging = this.player.isDodging;

    // Update player entity
    this.player.update(dt, input, this.dungeon.tiles);

    // Detect new dodge start for relic effects (M21)
    // Track dodge count for achievements (M28)
    if (!wasDodging && this.player.isDodging) {
      GameState.dodgeCount++;
    }

    if (!wasDodging && this.player.isDodging && this.relicSystem) {
      const dodgeEffects = this.relicSystem.onDodge(this.player, this.world, this.enemies);
      if (dodgeEffects.length > 0) {
        this._handleAbilityEffects(dodgeEffects);
      }
    }

    // If player attack is active, check hits each frame
    if (this.player.isAttacking && this.combatSystem.isAttackActive) {
      const hits = this.combatSystem.playerAttack(this.player, this.enemies);
      for (const enemy of hits) {
        const eCenterX = enemy.x + TILE_SIZE / 2;
        const eCenterY = enemy.y + TILE_SIZE / 2;

        // Impact particles (M26)
        const pCX = this.player.x + TILE_SIZE / 2;
        const pCY = this.player.y + TILE_SIZE / 2;
        const dx = eCenterX - pCX;
        const dy = eCenterY - pCY;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        this.particleSystem.impact(eCenterX, eCenterY, dx / dist, dy / dist, 0xffffff);

        // Light screen shake on hit (M26)
        this.screenShake.shake(1.5, 0.08);

        // Enemy hit flash to white (M26) — flash white then restore
        enemy.gfx.tint = 0xffffff;
        setTimeout(() => { if (enemy.gfx) enemy.gfx.tint = 0xffffff; }, 50);

        // Combo tracking (M26)
        const combo = this.comboSystem.registerHit();
        if (combo.count >= 3 && combo.count % 3 === 0) {
          this.damageNumbers.spawn(pCX, pCY - 24, combo.count + ' COMBO!', 0xffaa00);
        }

        // Relic on-damage-dealt (M21 - lifesteal)
        if (this.relicSystem) {
          const [minD, maxD] = this.player.getDamageRange();
          const avgDmg = Math.floor((minD + maxD) / 2);
          this.relicSystem.onDamageDealt(this.player, avgDmg);
        }
      }
    }

    // End swing when attack finishes
    if (!this.player.isAttacking && this.combatSystem.isAttackActive) {
      this.combatSystem.endSwing();
    }

    // Update enemies and check enemy attacks
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];

      enemy.update(dt, this.player.x, this.player.y, this.dungeon.tiles, this.pathfinding);

      // Check enemy attack on player
      this.combatSystem.enemyAttack(enemy, this.player);

      // Handle enemy death: remove from scene, award gold
      if (!enemy.isAlive) {
        // Death burst particles (M26)
        const deathX = enemy.x + TILE_SIZE / 2;
        const deathY = enemy.y + TILE_SIZE / 2;
        this.particleSystem.burst(deathX, deathY, {
          color: enemy.color,
          count: 8,
          speed: 50,
          size: 3,
          life: 0.5,
          gravity: 40,
        });
        // Shake on kill
        this.screenShake.shake(2, 0.1);

        this.world.removeChild(enemy.container);
        enemy.container.destroy();

        // Track enemies defeated (M7)
        GameState.enemiesDefeated++;

        // Award XP from enemy (M22)
        const xpReward = enemy.xpReward || 5;
        const levelResult = LevelSystem.addXP(GameState, xpReward);
        if (levelResult.leveledUp) {
          // Show level-up text
          const pCenterX = this.player.x + TILE_SIZE / 2;
          const pCenterY = this.player.y + TILE_SIZE / 2;
          this.damageNumbers.spawn(pCenterX, pCenterY - 20, 'LEVEL UP!', 0xffdd44);
          // Update player stats from new allocations
          this.player.statAllocations = { ...GameState.statAllocations };
          this.player._applyEffectiveStats();
          // Open level-up panel
          this._openLevelUpPanel();
        }

        // Trigger relic on-kill effects (M21)
        if (this.relicSystem) {
          this.relicSystem.onKill(this.player);
        }

        // Determine enemy type for loot rolling
        const isBoss = enemy.isBoss || (enemy instanceof Boss);
        const eCenterX = enemy.x + TILE_SIZE / 2;
        const eCenterY = enemy.y + TILE_SIZE / 2;

        // Roll loot table and create drops at death position (M9)
        const lootType = isBoss ? 'boss' : 'regular';
        const drops = LootTable.rollEnemyDrop(this.lootRng, lootType);
        for (const drop of drops) {
          // Small random offset so items don't stack exactly
          const offX = (Math.random() * 2 - 1) * 6;
          const offY = (Math.random() * 2 - 1) * 6;
          this.itemManager.addDrop(eCenterX + offX, eCenterY + offY, drop.itemData, drop.itemId);
        }

        // If no loot dropped, still show a small gold splash for feedback
        if (drops.length === 0) {
          const goldReward = isBoss ? (10 + Math.floor(Math.random() * 11)) : (1 + Math.floor(Math.random() * 3));
          GameState.gold += goldReward;
          GameState.goldCollected += goldReward;
          this.damageNumbers.spawn(eCenterX, eCenterY - 4, '+' + goldReward + ' gold', 0xffdd44);
        }

        // Track room clearing
        if (enemy.roomIndex !== undefined) {
          this._onEnemyKilledInRoom(enemy.roomIndex);
        }

        // Check if boss was defeated
        if (isBoss && enemy === this.boss) {
          this.bossDefeated = true;
          GameState.bossDefeatedThisRun = true;
          DebugLog.info('GameplayScene', 'Boss defeated! Stairs are now active.');

          // Show hint near stairs
          const bossRoom = this.dungeon.rooms.find(r => r.type === 'boss');
          if (bossRoom) {
            this.damageNumbers.spawn(
              bossRoom.centerX * TILE_SIZE + TILE_SIZE / 2,
              bossRoom.centerY * TILE_SIZE - TILE_SIZE,
              'Press E on stairs',
              0x44cc44
            );
          }
        }

        this.enemies.splice(i, 1);
      }
    }

    // Update projectiles and check collisions (M16)
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const proj = this.projectiles[i];
      const alive = proj.update(dt, this.dungeon.tiles);

      if (alive && proj.alive) {
        if (proj.fromEnemy) {
          // Check hit on player
          if (proj.checkHit(this.player)) {
            this.player.takeDamage(proj.damage, proj.x, proj.y);
            // Apply status effect from projectile (M19)
            if (proj.statusEffect && this.statusEffectSystem) {
              this.statusEffectSystem.apply(this.player, proj.statusEffect);
            }
            // Note: checkHit handles alive = false for non-piercing
          }
        } else {
          // Check hit on enemies
          for (const enemy of this.enemies) {
            if (!enemy.isAlive || enemy.state === 'dead') continue;
            if (proj.checkHit(enemy)) {
              // Check frozen bonus damage (M18)
              let dmg = proj.damage;
              if (this.statusEffectSystem && this.statusEffectSystem.isFrozen(enemy)) {
                dmg = Math.round(dmg * 1.5);
                this.statusEffectSystem.remove(enemy, 'frozen');
              }
              const dx = enemy.x + TILE_SIZE / 2 - proj.x;
              const dy = enemy.y + TILE_SIZE / 2 - proj.y;
              const dist = Math.sqrt(dx * dx + dy * dy) || 1;
              enemy.takeDamage(dmg, dx / dist, dy / dist);
              this.damageNumbers.spawn(enemy.x + TILE_SIZE / 2, enemy.y - 4, dmg, 0xffffff);
              // Apply status effect from projectile (M19)
              if (proj.statusEffect && this.statusEffectSystem) {
                this.statusEffectSystem.apply(enemy, proj.statusEffect);
              }
              // For non-piercing, break after first hit (checkHit sets alive=false)
              if (!proj.piercing || !proj.alive) break;
            }
          }
        }
      }

      if (!proj.alive) {
        if (proj.container.parent) {
          this.world.removeChild(proj.container);
        }
        proj.container.destroy();
        this.projectiles.splice(i, 1);
      }
    }

    // Sync player HP to GameState
    GameState.playerHP = this.player.hp;

    // Check player death -- transition to GameOverScene (M7)
    if (this.player.hp <= 0) {
      DebugLog.info('GameplayScene', 'Player died');

      // Record run end and save meta-progression (M10)
      GameState.recordRunEnd();
      GameState.saveMeta();
      GameState.clearRunSave();

      this.sceneManager.switchTo(new GameOverScene(this.sceneManager, this.eventBus));
      return;
    }

    // Consumable hotkeys 1-5 (M9)
    for (let i = 0; i < 5; i++) {
      if (input.isKeyJustPressed('Digit' + (i + 1))) {
        const result = this.player.useConsumable(i);
        if (result) {
          const pCenterX = this.player.x + TILE_SIZE / 2;
          const pCenterY = this.player.y + TILE_SIZE / 2;
          if (result.healed && result.healed > 0) {
            this.damageNumbers.spawn(pCenterX, pCenterY - 8, '+' + result.healed + ' HP', 0x44ff44);
          }
          // Sync to GameState
          GameState.inventory = this.player.inventory.slice();
          GameState.playerHP = this.player.hp;
        }
      }
    }

    // Update item drops and check pickups (M9)
    this.itemManager.update(dt);
    const pickedUp = this.itemManager.checkPickups(this.player.x, this.player.y);
    for (const item of pickedUp) {
      this._handlePickup(item);
    }

    // Update status effects on all entities (M18)
    if (this.statusEffectSystem) {
      const allEntities = [this.player, ...this.enemies.filter(e => e.isAlive)];
      this.statusEffectSystem.update(dt, allEntities);
    }

    // Update AoE system visuals (M20)
    if (this.aoeSystem) {
      this.aoeSystem.update(dt);
    }

    // Update ability visual effects (M12)
    this._updateAbilityEffects(dt);

    // Update damage numbers
    this.damageNumbers.update(dt);

    // Track room changes
    this._updateCurrentRoom();

    // Update minimap (M23)
    if (this.minimap) {
      const currentRoomIdx = this.currentRoom ? this.dungeon.rooms.indexOf(this.currentRoom) : -1;
      this.minimap.update(this.player.x, this.player.y, currentRoomIdx);
    }

    // Check achievements periodically (M28)
    if (this.achievementSystem) {
      this.achievementSystem.check({
        combo: this.comboSystem ? this.comboSystem.count : 0,
        dodgeCount: GameState.dodgeCount,
        bossDefeated: GameState.bossDefeatedThisRun,
      });
      // Show toast for newly unlocked achievements
      let toast;
      while ((toast = this.achievementSystem.popToast())) {
        this.achievementPanel.showToast(toast);
      }
    }

    // Update camera
    this._updateCamera();

    // Update HUD with scene data
    this._updateHUD();
  }

  /**
   * Update HUD display (extracted for reuse when inventory is open).
   */
  _updateHUD() {
    const abilityInfo = this.abilitySystem.getAbilityInfo(this.player.classId);

    // Boss health bar data (M17)
    const bossAlive = this.boss && this.boss.isAlive && this.boss.state !== 'dead';
    const inBossRoom = this.currentRoom && this.currentRoom.type === 'boss';

    this.hud.update(GameState, {
      roomsCleared: this.roomsCleared,
      totalRooms: this.totalClearableRooms,
      weaponName: this.player.weapon ? this.player.weapon.name : null,
      inventory: this.player.inventory,
      className: this.player.classData ? this.player.classData.name : null,
      classColor: this.player.classData ? this.player.classData.color : null,
      abilityName: abilityInfo ? abilityInfo.name : null,
      abilityCooldown: this.abilitySystem.getCooldown(this.player.classId),
      abilityReady: this.abilitySystem.isReady(this.player.classId),
      shield: this.player.shield,
      classId: this.player.classId,
      // Status effects (M18)
      statusEffects: this.statusEffectSystem ? this.statusEffectSystem.getActiveEffects(this.player).map(fx => {
        const config = { burning: 0xff4400, frozen: 0x88ccff, poisoned: 0x44cc44, stunned: 0xffff00 };
        return { ...fx, color: config[fx.id] || 0xffffff };
      }) : [],
      // Boss health bar (M17)
      bossAlive: bossAlive && inBossRoom,
      bossName: this.boss ? this.boss.name : null,
      bossHp: this.boss ? this.boss.hp : 0,
      bossMaxHp: this.boss ? this.boss.maxHp : 0,
      // Level system (M22)
      level: GameState.level,
      xp: GameState.xp,
      xpToNext: GameState.xpToNext,
      // Relics (M21)
      relics: this.relicSystem ? this.relicSystem.getRelics() : [],
    });
  }

  destroy() {
    if (this.itemManager) {
      this.itemManager.clear();
      this.itemManager = null;
    }
    if (this.damageNumbers) {
      this.damageNumbers.destroy();
      this.damageNumbers = null;
    }
    if (this.hud) {
      this.hud.destroy();
      this.hud = null;
    }
    if (this.inventoryPanel) {
      this.inventoryPanel.destroy();
      this.inventoryPanel = null;
    }
    if (this.shopPanel) {
      this.shopPanel.destroy();
      this.shopPanel = null;
    }
    if (this.levelUpPanel) {
      this.levelUpPanel.destroy();
      this.levelUpPanel = null;
    }
    if (this.minimap) {
      this.minimap.destroy();
      this.minimap = null;
    }
    this.relicSystem = null;
    if (this.aoeSystem) {
      this.aoeSystem.destroy();
      this.aoeSystem = null;
    }
    this.statusEffectSystem = null;
    if (this.particleSystem) {
      this.particleSystem.destroy();
      this.particleSystem = null;
    }
    this.comboSystem = null;
    this.screenShake = null;
    this.achievementSystem = null;
    if (this.skillTreePanel) {
      this.skillTreePanel.destroy();
      this.skillTreePanel = null;
    }
    if (this.achievementPanel) {
      this.achievementPanel.destroy();
      this.achievementPanel = null;
    }
  }
}
